/**
 * Form Validation Schemas
 * WHY: Centralized validation with zod for type-safe, secure input validation
 */

import { z } from "zod";

// T573 (cc_tasks row 533, 2026-10-08): nothing in sign-up may stop a real person. The only checks
// left are the ones that protect us: an email address that can receive the confirmation, and a
// password the account service accepts (register-account refuses under 8 characters; passwords are
// stored with bcrypt, which reads at most 72 bytes). Names accept any script, digits, dots,
// brackets, apostrophes and accents: trimmed, limited in length, nothing else. Every message says
// what to do.

// Email validation with proper security checks
export const emailSchema = z
  .string()
  .trim()
  .min(1, "Enter your email address.")
  .email("Enter your full email address, like name@company.com.")
  .max(254, "That email address is too long. Use one of 254 characters or fewer.")
  .toLowerCase();

// Password: what the account service accepts, nothing more.
export const passwordSchema = z
  .string()
  .min(8, "Use a password of at least 8 characters.")
  .refine((p) => new TextEncoder().encode(p).length <= 72, "Use a shorter password: 72 characters or fewer.");

// Simpler password for login (don't enforce rules on existing passwords)
export const loginPasswordSchema = z
  .string()
  .min(1, "Enter your password.")
  .max(128, "That password is too long. Check it and try again.");

// Full name: any name a person has, in any script. Trimmed; length is the only limit.
export const fullNameSchema = z
  .string()
  .trim()
  .min(1, "Enter your name, as you would like it shown.")
  .max(100, "Use a name of 100 characters or fewer.");

// Company name validation
export const companyNameSchema = z
  .string()
  .trim()
  .max(100, "Use a company name of 100 characters or fewer.")
  .optional();

// Sign up form schema
export const signUpSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  fullName: fullNameSchema,
  companyName: companyNameSchema,
});

// Sign in form schema
export const signInSchema = z.object({
  email: emailSchema,
  password: loginPasswordSchema,
});

// Magic link form schema
export const magicLinkSchema = z.object({
  email: emailSchema,
});

// Types
export type SignUpFormData = z.infer<typeof signUpSchema>;
export type SignInFormData = z.infer<typeof signInSchema>;
export type MagicLinkFormData = z.infer<typeof magicLinkSchema>;
